const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });
  animationTriggerElements.forEach((element) => observer.observe(element));
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!elementIsVisible) return;

        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      }),
      { passive: true }
    );
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}

/* ZB merged hero and tiny-size slider - draft theme only. */
(() => {
  if (window.__zbCombinedHeroLoaded) return;
  window.__zbCombinedHeroLoaded = true;

  const AUTOPLAY_MS = 5000;
  const IS_DESIGN_MODE = Boolean(window.Shopify?.designMode);
  const HEADING_SELECTOR = 'h1, h2, h3, [role="heading"]';
  const BODY_SELECTOR = 'p, .rte, [class*="description"], [class*="text"]';
  const STYLE_ID = 'zb-combined-hero-styles-v1';
  let activeCleanup = () => {};
  let rebuildTimer;

  const normalize = (value = '') =>
    value
      .replace(/[’‘]/g, "'")
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

  const stripDuplicateAttributes = (root) => {
    if (!(root instanceof Element)) return;
    root.removeAttribute('id');
    root.removeAttribute('data-shopify-editor-block');
    root.querySelectorAll('[id], [data-shopify-editor-block]').forEach((node) => {
      node.removeAttribute('id');
      node.removeAttribute('data-shopify-editor-block');
    });
    root.querySelectorAll('script, noscript').forEach((node) => node.remove());
  };

  const prepareSectionClone = (clone, source) => {
    const sourceNodes = [source, ...source.querySelectorAll('[id]')];
    sourceNodes.forEach((node) => {
      if (!node.id) return;
      node.dataset.zbOriginalId = node.id;
      node.id = `${node.id}--zb-source`;
    });
    const editorAttributes = [
      'data-shopify-editor-block',
      'data-shopify-editor-section',
      'data-section-id',
      'data-section-type',
    ];
    editorAttributes.forEach((attribute) => clone.removeAttribute(attribute));
    clone
      .querySelectorAll('[data-shopify-editor-block], [data-shopify-editor-section], [data-section-id], [data-section-type]')
      .forEach((node) => editorAttributes.forEach((attribute) => node.removeAttribute(attribute)));
    clone.querySelectorAll('script, noscript').forEach((node) => node.remove());
  };

  const findHeading = (patterns) =>
    Array.from(document.querySelectorAll(HEADING_SELECTOR)).find((element) => {
      if (element.closest('[data-zb-combined-hero]')) return false;
      const text = normalize(element.textContent);
      return patterns.some((pattern) => pattern.test(text));
    });

  const getSectionRoot = (element) =>
    element?.closest('.shopify-section') || element?.closest('section') || element?.parentElement;

  const findBodyCopy = (section) => {
    const paragraphs = Array.from(section.querySelectorAll('p'));
    const matches = paragraphs.length ? paragraphs : Array.from(section.querySelectorAll(BODY_SELECTOR));
    const preferred = matches.find((element) => {
      const text = normalize(element.textContent);
      return text.includes('designed with love and care') || text.includes('tiny sizes covered');
    });
    const fallback = matches.find((element) => normalize(element.textContent).length >= 60);
    return preferred || fallback;
  };

  const imageLooksUsable = (image) => {
    const source = image.currentSrc || image.getAttribute('src') || image.getAttribute('srcset') || '';
    const context = normalize(`${image.getAttribute('alt') || ''} ${image.parentElement?.textContent || ''}`);
    return Boolean(source) && !/(logo|icon|payment|whatsapp|arrow|chevron)/.test(context);
  };

  const imageContext = (image, section) => {
    let node = image;
    let depth = 0;
    let text = image.getAttribute('alt') || '';
    while (node?.parentElement && node.parentElement !== section && depth < 5) {
      node = node.parentElement;
      text += ` ${node.textContent || ''}`;
      depth += 1;
    }
    return normalize(text);
  };

  const collectStageImages = (section) => {
    const available = Array.from(section.querySelectorAll('img')).filter(imageLooksUsable);
    const used = new Set();
    const stages = [
      { label: 'Preemie', pattern: /(micro preemie|tiny preemie|preemie|premature)/ },
      { label: 'Low Birth Weight', pattern: /(low birth weight|low-birth-weight|lbw|prem[- ]?1|prem[- ]?2)/ },
      { label: 'Newborn', pattern: /(newborn|new born|early baby)/ },
    ];

    return stages.map((stage) => {
      let image = available.find((candidate) => !used.has(candidate) && stage.pattern.test(imageContext(candidate, section)));
      if (!image) image = available.find((candidate) => !used.has(candidate));
      if (image) used.add(image);
      return { ...stage, image };
    });
  };

  const makeCardIcon = (label) => {
    const icon = document.createElement('span');
    icon.className = 'zb-combined-hero__card-icon';
    icon.setAttribute('aria-hidden', 'true');
    if (label === 'Newborn') {
      icon.innerHTML = '<svg viewBox="0 0 24 24"><path d="M12 3a8.5 8.5 0 1 0 8.5 8.5A6.8 6.8 0 0 1 12 3Z"/><path d="M17.8 3.9v2.7M16.45 5.25h2.7"/></svg>';
    } else if (label === 'Low Birth Weight') {
      icon.innerHTML = '<svg viewBox="0 0 24 24"><path d="M12 20s-7-4.2-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.8-7 10-7 10Z"/><path d="M9.5 12h5"/></svg>';
    } else {
      icon.innerHTML = '<svg viewBox="0 0 24 24"><path d="M7.2 18.8c2.5 1.7 7.1 1.7 9.6 0M6 8.3A6 6 0 0 1 18 8.3c0 4.3-2.7 8.2-6 8.2S6 12.6 6 8.3Z"/><path d="M9.2 9.7h.1M14.7 9.7h.1M10 12.7c1.2.8 2.8.8 4 0"/></svg>';
    }
    return icon;
  };

  const createStageCard = ({ label, image }, index) => {
    const linkedParent = image?.closest('a[href]');
    const card = document.createElement(linkedParent ? 'a' : 'article');
    card.className = `zb-combined-hero__card zb-combined-hero__card--${index + 1}`;
    card.setAttribute('aria-label', `${label} sizes`);
    if (linkedParent) card.setAttribute('href', linkedParent.getAttribute('href'));

    const media = document.createElement('div');
    media.className = 'zb-combined-hero__card-media';
    if (image) {
      const clonedImage = image.cloneNode(true);
      stripDuplicateAttributes(clonedImage);
      clonedImage.setAttribute('loading', 'eager');
      clonedImage.setAttribute('decoding', 'async');
      clonedImage.setAttribute('alt', `${label} baby clothing size`);
      media.appendChild(clonedImage);
    } else {
      media.classList.add('zb-combined-hero__card-media--fallback');
      media.appendChild(makeCardIcon(label));
    }

    const labelPanel = document.createElement('span');
    labelPanel.className = 'zb-combined-hero__card-label';
    labelPanel.appendChild(makeCardIcon(label));
    const labelText = document.createElement('span');
    labelText.textContent = label;
    labelPanel.appendChild(labelText);

    card.append(media, labelPanel);
    return card;
  };

  const createButtonRow = (sourceSection) => {
    const sourceButtons = Array.from(sourceSection.querySelectorAll('a[href]')).filter((link) => {
      const text = normalize(link.textContent);
      return text && text.length <= 45 && !/(account|login|search|cart)/.test(text);
    });
    const unique = [];
    sourceButtons.forEach((button) => {
      const key = `${button.getAttribute('href')}|${normalize(button.textContent)}`;
      if (!unique.some((item) => item.key === key) && unique.length < 2) unique.push({ key, button });
    });
    if (!unique.length) return null;

    const row = document.createElement('div');
    row.className = 'zb-combined-hero__actions';
    unique.forEach(({ button }, index) => {
      const link = document.createElement('a');
      link.className = `zb-combined-hero__button${index === 1 ? ' zb-combined-hero__button--secondary' : ''}`;
      link.href = button.getAttribute('href');
      link.textContent = button.textContent.trim();
      row.appendChild(link);
    });
    return row;
  };

  const injectStyles = () => {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      [data-zb-merged-source] { display: none !important; }
      .zb-combined-hero {
        --zb-navy: #123b5d;
        --zb-sky: #73cbea;
        --zb-ice: #f1fafe;
        --zb-soft-blue: #e7f6fc;
        --zb-border: #d7eaf2;
        --zb-text: #122333;
        --zb-muted: #617283;
        position: relative;
        width: min(calc(100% - 64px), 1320px);
        margin: 18px auto 48px;
        overflow: hidden;
        color: var(--zb-text);
        background: linear-gradient(130deg, #ffffff 0%, #ffffff 42%, var(--zb-ice) 100%);
        border: 1px solid var(--zb-border);
        border-radius: 16px;
        box-shadow: 0 14px 38px rgba(18, 59, 93, 0.07);
        font-family: var(--font-body-family, 'Nunito Sans', Inter, system-ui, Arial, sans-serif);
      }
      .zb-combined-hero__viewport { overflow: hidden; }
      .zb-combined-hero__track {
        display: flex;
        align-items: flex-start;
        transition: transform 620ms cubic-bezier(0.22, 1, 0.36, 1), height 360ms ease;
        will-change: transform;
      }
      .zb-combined-hero__slide {
        flex: 0 0 100%;
        min-width: 0;
        padding: clamp(28px, 3vw, 44px);
      }
      .zb-combined-hero__slide--brand {
        display: grid;
        grid-template-columns: minmax(300px, 0.82fr) minmax(0, 1.48fr);
        gap: clamp(22px, 2.6vw, 38px);
        align-items: center;
        min-height: 0;
      }
      .zb-combined-hero__copy { max-width: 520px; }
      .zb-combined-hero__eyebrow {
        display: inline-flex;
        align-items: center;
        min-height: 30px;
        margin: 0 0 14px;
        padding: 5px 12px;
        color: var(--zb-navy);
        background: var(--zb-soft-blue);
        border: 1px solid var(--zb-border);
        border-radius: 999px;
        font-size: 13px;
        font-weight: 700;
        line-height: 18px;
      }
      .zb-combined-hero__title {
        margin: 0;
        color: var(--zb-navy);
        font-family: inherit;
        font-size: 48px;
        font-weight: 800;
        letter-spacing: -0.02em;
        line-height: 56px;
      }
      .zb-combined-hero__body {
        margin: 18px 0 0;
        color: var(--zb-text);
        font-size: 16px;
        font-weight: 400;
        line-height: 24px;
      }
      .zb-combined-hero__actions {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        margin-top: 24px;
      }
      .zb-combined-hero__button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-height: 46px;
        padding: 11px 22px;
        color: #ffffff;
        background: var(--zb-navy);
        border: 1px solid var(--zb-navy);
        border-radius: 999px;
        font-size: 15px;
        font-weight: 800;
        line-height: 20px;
        text-decoration: none;
      }
      .zb-combined-hero__button:hover { color: #ffffff; background: #0d2f4b; }
      .zb-combined-hero__button--secondary { color: var(--zb-navy); background: #ffffff; border-color: var(--zb-border); }
      .zb-combined-hero__button--secondary:hover { color: var(--zb-navy); background: var(--zb-ice); }
      .zb-combined-hero__cards {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: clamp(12px, 1.4vw, 20px);
        align-items: stretch;
      }
      .zb-combined-hero__card {
        position: relative;
        display: block;
        min-width: 0;
        aspect-ratio: 0.72;
        overflow: hidden;
        color: var(--zb-navy);
        background: #f6fafc;
        border: 1px solid var(--zb-border);
        border-radius: 16px;
        box-shadow: 0 10px 24px rgba(18, 59, 93, 0.07);
        text-decoration: none;
      }
      .zb-combined-hero__card-media { position: absolute; inset: 0; background: #f6fafc; }
      .zb-combined-hero__card-media img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
      }
      .zb-combined-hero__card-media--fallback {
        display: grid;
        place-items: center;
        background: linear-gradient(155deg, #ffffff, var(--zb-soft-blue));
      }
      .zb-combined-hero__card-media--fallback > .zb-combined-hero__card-icon { width: 62px; height: 62px; opacity: 0.45; }
      .zb-combined-hero__card-label {
        position: absolute;
        right: 0;
        bottom: 0;
        left: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 9px;
        min-height: 72px;
        padding: 12px;
        color: var(--zb-navy);
        background: rgba(255, 255, 255, 0.94);
        border-top: 1px solid var(--zb-border);
        backdrop-filter: blur(8px);
        font-size: 16px;
        font-weight: 700;
        line-height: 22px;
        text-align: center;
      }
      .zb-combined-hero__card--1 .zb-combined-hero__card-label { background: rgba(241, 250, 254, 0.95); }
      .zb-combined-hero__card--2 .zb-combined-hero__card-label { background: rgba(255, 248, 237, 0.95); }
      .zb-combined-hero__card--3 .zb-combined-hero__card-label { background: rgba(216, 241, 228, 0.95); }
      .zb-combined-hero__card-icon { display: inline-flex; width: 27px; height: 27px; flex: 0 0 27px; }
      .zb-combined-hero__card-icon svg { width: 100%; height: 100%; fill: none; stroke: currentColor; stroke-linecap: round; stroke-linejoin: round; stroke-width: 1.55; }
      .zb-combined-hero__slide--sizes {
        display: flex;
        align-items: center;
        min-height: 0;
        background: #ffffff;
      }
      .zb-combined-hero__size-content { width: 100%; }
      .zb-combined-hero__size-content > .shopify-section,
      .zb-combined-hero__size-content > section { margin: 0; }
      .zb-combined-hero__arrow {
        position: absolute;
        z-index: 3;
        top: 50%;
        display: grid;
        width: 44px;
        height: 44px;
        place-items: center;
        padding: 0;
        color: var(--zb-navy);
        background: rgba(255, 255, 255, 0.92);
        border: 1px solid var(--zb-border);
        border-radius: 999px;
        box-shadow: 0 6px 18px rgba(18, 59, 93, 0.1);
        transform: translateY(-50%);
        cursor: pointer;
      }
      .zb-combined-hero__arrow:hover { background: var(--zb-ice); }
      .zb-combined-hero__arrow--previous { left: 12px; }
      .zb-combined-hero__arrow--next { right: 12px; }
      .zb-combined-hero__arrow svg { width: 20px; height: 20px; fill: none; stroke: currentColor; stroke-linecap: round; stroke-linejoin: round; stroke-width: 2; }
      .zb-combined-hero__dots {
        position: absolute;
        z-index: 3;
        right: 0;
        bottom: 17px;
        left: 0;
        display: flex;
        justify-content: center;
        gap: 8px;
        pointer-events: none;
      }
      .zb-combined-hero__dot {
        width: 9px;
        height: 9px;
        padding: 0;
        background: #ffffff;
        border: 1px solid var(--zb-navy);
        border-radius: 999px;
        pointer-events: auto;
        cursor: pointer;
      }
      .zb-combined-hero__dot[aria-current='true'] { width: 26px; background: var(--zb-navy); }
      .zb-combined-hero :focus-visible { outline: 3px solid var(--zb-sky); outline-offset: 3px; }
      @media (max-width: 989px) {
        .zb-combined-hero__slide--brand { grid-template-columns: minmax(250px, 0.8fr) minmax(0, 1.2fr); gap: 24px; min-height: 0; }
        .zb-combined-hero__title { font-size: 40px; line-height: 48px; }
        .zb-combined-hero__card-label { font-size: 15px; line-height: 21px; }
      }
      @media (max-width: 749px) {
        .zb-combined-hero {
          width: calc(100% - 32px);
          margin: 10px auto 30px;
          border-radius: 14px;
          box-shadow: 0 10px 26px rgba(18, 59, 93, 0.06);
        }
        .zb-combined-hero__slide { padding: 24px 16px 54px; }
        .zb-combined-hero__slide--brand { display: block; min-height: auto; }
        .zb-combined-hero__copy { max-width: none; }
        .zb-combined-hero__eyebrow { margin-bottom: 12px; }
        .zb-combined-hero__title { font-size: 34px; line-height: 40px; }
        .zb-combined-hero__body { margin-top: 14px; font-size: 15px; line-height: 23px; }
        .zb-combined-hero__actions { margin-top: 18px; }
        .zb-combined-hero__button { min-height: 44px; padding: 10px 18px; }
        .zb-combined-hero__cards {
          display: flex;
          gap: 12px;
          margin: 24px -16px 0;
          padding: 0 16px 8px;
          overflow-x: auto;
          overscroll-behavior-inline: contain;
          scroll-padding-inline: 16px;
          scroll-snap-type: x mandatory;
          scrollbar-width: none;
        }
        .zb-combined-hero__cards::-webkit-scrollbar { display: none; }
        .zb-combined-hero__card {
          flex: 0 0 min(84vw, 340px);
          width: min(84vw, 340px);
          height: 500px;
          height: clamp(440px, 64svh, 560px);
          min-height: 440px;
          max-height: 560px;
          aspect-ratio: auto;
          scroll-snap-align: start;
          border-radius: 14px;
        }
        .zb-combined-hero__card-media img { object-position: center top; }
        .zb-combined-hero__card-label { min-height: 66px; font-size: 15px; line-height: 21px; }
        .zb-combined-hero__slide--sizes { min-height: auto; padding: 10px 0 54px; }
        .zb-combined-hero__size-content .page-width { padding-right: 16px; padding-left: 16px; }
        .zb-combined-hero__arrow { display: none; }
        .zb-combined-hero__dots { bottom: 14px; }
      }
      @media (prefers-reduced-motion: reduce) {
        .zb-combined-hero__track { transition: none; }
      }
    `;
    document.head.appendChild(style);
  };

  const createArrow = (direction) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `zb-combined-hero__arrow zb-combined-hero__arrow--${direction}`;
    button.setAttribute('aria-label', direction === 'previous' ? 'Previous hero slide' : 'Next hero slide');
    button.innerHTML = direction === 'previous'
      ? '<svg viewBox="0 0 24 24"><path d="m15 18-6-6 6-6"/></svg>'
      : '<svg viewBox="0 0 24 24"><path d="m9 18 6-6-6-6"/></svg>';
    return button;
  };

  const build = () => {
    if (document.querySelector('[data-zb-combined-hero]')) return;

    const brandHeading = findHeading([/pakistan'?s first/, /preemie.*nicu.*friendly.*clothing store/]);
    const sizeHeading = findHeading([/understanding tiny sizes/, /every tiny baby is different/]);
    const brandSection = getSectionRoot(brandHeading);
    const sizeSection = getSectionRoot(sizeHeading);
    if (!brandHeading || !sizeHeading || !brandSection || !sizeSection || brandSection === sizeSection) return;

    injectStyles();

    const root = document.createElement('section');
    root.className = 'zb-combined-hero';
    root.dataset.zbCombinedHero = 'v1';
    root.setAttribute('aria-roledescription', 'carousel');
    root.setAttribute('aria-label', 'ZB introduction and tiny-size guide');

    const viewport = document.createElement('div');
    viewport.className = 'zb-combined-hero__viewport';
    const track = document.createElement('div');
    track.className = 'zb-combined-hero__track';

    const brandSlide = document.createElement('div');
    brandSlide.className = 'zb-combined-hero__slide zb-combined-hero__slide--brand';
    brandSlide.setAttribute('role', 'group');
    brandSlide.setAttribute('aria-roledescription', 'slide');
    brandSlide.setAttribute('aria-label', '1 of 2');

    const copy = document.createElement('div');
    copy.className = 'zb-combined-hero__copy';
    const eyebrow = document.createElement('p');
    eyebrow.className = 'zb-combined-hero__eyebrow';
    eyebrow.textContent = 'Made for every tiny beginning';
    const title = document.createElement('h1');
    title.className = 'zb-combined-hero__title';
    title.textContent = brandHeading.textContent.replace(/First(?=Preemie)/i, 'First ').trim();
    const bodySource = findBodyCopy(brandSection);
    const body = document.createElement('p');
    body.className = 'zb-combined-hero__body';
    body.textContent = bodySource?.textContent.trim() || 'Designed with love and care. By assessing the problems in NICU and discussing with professionals, our collection includes high-quality and adorable outfits that are made exclusively for babies born early. We’ve got all tiny sizes covered.';
    copy.append(eyebrow, title, body);
    const actions = createButtonRow(brandSection);
    if (actions) copy.appendChild(actions);

    const cards = document.createElement('div');
    cards.className = 'zb-combined-hero__cards';
    cards.setAttribute('aria-label', 'Shop by baby stage');
    collectStageImages(sizeSection).forEach((stage, index) => cards.appendChild(createStageCard(stage, index)));
    brandSlide.append(copy, cards);

    const sizeSlide = document.createElement('div');
    sizeSlide.className = 'zb-combined-hero__slide zb-combined-hero__slide--sizes';
    sizeSlide.setAttribute('role', 'group');
    sizeSlide.setAttribute('aria-roledescription', 'slide');
    sizeSlide.setAttribute('aria-label', '2 of 2');
    sizeSlide.setAttribute('aria-hidden', 'true');
    const sizeContent = document.createElement('div');
    sizeContent.className = 'zb-combined-hero__size-content';
    const sizeClone = sizeSection.cloneNode(true);
    prepareSectionClone(sizeClone, sizeSection);
    sizeContent.appendChild(sizeClone);
    sizeSlide.appendChild(sizeContent);

    track.append(brandSlide, sizeSlide);
    viewport.appendChild(track);
    root.appendChild(viewport);

    const previous = createArrow('previous');
    const next = createArrow('next');
    const dots = document.createElement('div');
    dots.className = 'zb-combined-hero__dots';
    dots.setAttribute('aria-label', 'Choose hero slide');
    const dotButtons = [0, 1].map((index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'zb-combined-hero__dot';
      dot.setAttribute('aria-label', `Show slide ${index + 1}`);
      dot.setAttribute('aria-current', index === 0 ? 'true' : 'false');
      dots.appendChild(dot);
      return dot;
    });
    root.append(previous, next, dots);

    const sources = [brandSection, sizeSection];
    sources.forEach((source) => {
      source.hidden = true;
      source.dataset.zbMergedSource = 'true';
    });
    brandSection.parentNode.insertBefore(root, brandSection);

    const slides = [brandSlide, sizeSlide];
    let activeIndex = 0;
    let autoplayId;
    let pointerStartX = null;
    let slideResizeObserver;

    const setTrackHeight = () => {
      const activeSlide = slides[activeIndex];
      if (!activeSlide?.isConnected) return;
      track.style.height = `${Math.ceil(activeSlide.scrollHeight)}px`;
    };

    const showSlide = (nextIndex, userInitiated = false) => {
      activeIndex = (nextIndex + slides.length) % slides.length;
      track.style.transform = `translate3d(-${activeIndex * 100}%, 0, 0)`;
      slides.forEach((slide, index) => slide.setAttribute('aria-hidden', index === activeIndex ? 'false' : 'true'));
      dotButtons.forEach((dot, index) => dot.setAttribute('aria-current', index === activeIndex ? 'true' : 'false'));
      window.requestAnimationFrame(setTrackHeight);
      if (userInitiated) restartAutoplay();
    };

    const stopAutoplay = () => {
      if (autoplayId) window.clearInterval(autoplayId);
      autoplayId = undefined;
    };

    const startAutoplay = () => {
      stopAutoplay();
      if (IS_DESIGN_MODE || window.matchMedia('(prefers-reduced-motion: reduce)').matches || document.hidden) return;
      autoplayId = window.setInterval(() => showSlide(activeIndex + 1), AUTOPLAY_MS);
    };

    const restartAutoplay = () => {
      stopAutoplay();
      startAutoplay();
    };

    previous.addEventListener('click', () => showSlide(activeIndex - 1, true));
    next.addEventListener('click', () => showSlide(activeIndex + 1, true));
    dotButtons.forEach((dot, index) => dot.addEventListener('click', () => showSlide(index, true)));
    root.addEventListener('mouseenter', stopAutoplay);
    root.addEventListener('mouseleave', startAutoplay);
    root.addEventListener('focusin', stopAutoplay);
    root.addEventListener('focusout', startAutoplay);
    root.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') showSlide(activeIndex - 1, true);
      if (event.key === 'ArrowRight') showSlide(activeIndex + 1, true);
    });
    viewport.addEventListener('touchstart', (event) => {
      pointerStartX = event.touches[0]?.clientX ?? null;
      stopAutoplay();
    }, { passive: true });
    viewport.addEventListener('touchend', (event) => {
      if (pointerStartX === null) return startAutoplay();
      const distance = (event.changedTouches[0]?.clientX ?? pointerStartX) - pointerStartX;
      if (Math.abs(distance) >= 48) showSlide(activeIndex + (distance < 0 ? 1 : -1), true);
      else startAutoplay();
      pointerStartX = null;
    }, { passive: true });
    const onVisibilityChange = () => document.hidden ? stopAutoplay() : startAutoplay();
    const onWindowResize = () => window.requestAnimationFrame(setTrackHeight);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('resize', onWindowResize, { passive: true });

    if ('ResizeObserver' in window) {
      slideResizeObserver = new ResizeObserver(() => window.requestAnimationFrame(setTrackHeight));
      slides.forEach((slide) => slideResizeObserver.observe(slide));
    }

    activeCleanup = () => {
      stopAutoplay();
      slideResizeObserver?.disconnect();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('resize', onWindowResize);
    };

    showSlide(0);
    startAutoplay();
  };

  const restoreSourceSections = () => {
    document.querySelectorAll('[data-zb-merged-source]').forEach((source) => {
      source.hidden = false;
      delete source.dataset.zbMergedSource;
      [source, ...source.querySelectorAll('[data-zb-original-id]')].forEach((node) => {
        if (!node.dataset.zbOriginalId) return;
        node.id = node.dataset.zbOriginalId;
        delete node.dataset.zbOriginalId;
      });
    });
  };

  const rebuild = () => {
    activeCleanup();
    document.querySelector('[data-zb-combined-hero]')?.remove();
    restoreSourceSections();
    window.requestAnimationFrame(build);
  };

  const requestEditorRebuild = () => {
    window.clearTimeout(rebuildTimer);
    rebuildTimer = window.setTimeout(rebuild, 120);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', build, { once: true });
  else build();

  if (IS_DESIGN_MODE) {
    document.addEventListener('shopify:section:load', requestEditorRebuild);
    document.addEventListener('shopify:section:unload', requestEditorRebuild);
    document.addEventListener('shopify:section:reorder', requestEditorRebuild);
  }

})();
